import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CalendarDays, Scale, ListChecks, Building2 } from "lucide-react";
import { PageShell, Eyebrow } from "../components/Layout";
import { REGULATORS } from "../data/mock";

export const AustraliaHub = () => (
  <PageShell>
    <section className="bg-ink text-white relative overflow-hidden">
      <div className="absolute inset-0 hero-grid-dark pointer-events-none" />
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 pt-16 pb-14 relative">
        <Eyebrow light>Australia Regulatory Hub</Eyebrow>
        <h1 className="font-heading text-5xl lg:text-[60px] font-bold tracking-tight leading-[1.02]">Eight regulators.<br />One operating manual.</h1>
        <p className="mt-6 text-[16px] text-white/60 max-w-2xl leading-relaxed">Control catalogues, applicability matrices, compliance calendars and penalty frameworks — APRA, ASIC, OAIC, AUSTRAC, ACSC, RBA, Home Affairs and the AASB climate regime, decoded for practitioners.</p>
        <div className="mt-8 flex flex-wrap gap-3 font-mono text-[11.5px]">
          {["8 regulators", "11 SOCI sectors", "12 templates · 6 free", "CPS 230 live — Jul 2025"].map((t) => (
            <span key={t} className="border border-white/20 text-white/70 px-3 py-1.5 rounded-sm">{t}</span>
          ))}
        </div>
      </div>
    </section>
    <section className="py-16">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-5">
        {REGULATORS.map((r) => (
          <Link key={r.slug} to={`/australia/${r.slug}`} data-testid={`hub-regulator-${r.slug}`}
            className="group border border-neutral-200 rounded-lg p-7 hover:border-accent/40 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(15,27,45,0.08)] transition-[border-color,transform,box-shadow] duration-200 bg-white">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent">{r.tag}</span>
              <ArrowUpRight size={16} className="text-neutral-300 group-hover:text-accent transition-colors duration-200" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-ink mt-3">{r.name}</h2>
            <p className="text-[13px] text-ink/45 mt-1">{r.title}</p>
            <p className="font-mono text-[11.5px] text-ink/55 mt-3">{r.detail}</p>
            <p className="text-[13.5px] text-ink/60 leading-relaxed mt-4 pt-4 border-t border-neutral-100">{r.scope}</p>
          </Link>
        ))}
      </div>
    </section>
  </PageShell>
);

export const RegulatorDetail = () => {
  const { slug } = useParams();
  const r = REGULATORS.find((x) => x.slug === slug) || REGULATORS[0];

  return (
    <PageShell>
      <section className="bg-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 hero-grid-dark pointer-events-none" />
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 pt-14 pb-12 relative">
          <Link to="/australia" className="inline-flex items-center gap-1.5 font-mono text-[11.5px] text-white/40 hover:text-accent-light transition-colors duration-150 mb-8" data-testid="back-to-hub">
            <ArrowLeft size={13} /> AUSTRALIA HUB
          </Link>
          <span className="block font-mono text-[11px] tracking-[0.25em] uppercase text-accent-light mb-3">{r.tag}</span>
          <h1 className="font-heading text-5xl lg:text-[56px] font-bold tracking-tight leading-[1.02]">{r.name}</h1>
          <p className="mt-3 text-[17px] text-white/55">{r.title}</p>
          <p className="font-mono text-[12px] text-white/40 mt-4">{r.detail}</p>
        </div>
      </section>
      <section className="py-14">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 space-y-8">
          <div className="border border-neutral-200 rounded-lg p-7">
            <div className="flex items-center gap-2.5 mb-4">
              <Building2 size={17} className="text-accent" />
              <h2 className="font-heading text-xl font-bold text-ink">Who it applies to</h2>
            </div>
            <p className="text-[15px] text-ink/70 leading-[1.8]">{r.scope}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-neutral-200 rounded-lg p-7">
              <div className="flex items-center gap-2.5 mb-5">
                <ListChecks size={17} className="text-accent" />
                <h2 className="font-heading text-xl font-bold text-ink">Key instruments</h2>
              </div>
              <ul className="space-y-3.5">
                {r.keyRules.map((k, i) => (
                  <li key={i} className="flex gap-3 text-[14px] text-ink/70 leading-relaxed">
                    <span className="font-mono text-[11px] text-accent mt-1 shrink-0">{String(i + 1).padStart(2, "0")}</span>{k}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-6">
              <div className="border border-neutral-200 rounded-lg p-7">
                <div className="flex items-center gap-2.5 mb-5">
                  <CalendarDays size={17} className="text-accent" />
                  <h2 className="font-heading text-xl font-bold text-ink">Compliance calendar</h2>
                </div>
                <ul className="space-y-3">
                  {r.deadlines.map((d, i) => (
                    <li key={i} className="font-mono text-[12.5px] text-ink/70 border-l-2 border-accent pl-3 py-0.5">{d}</li>
                  ))}
                </ul>
              </div>
              <div className="border-l-[3px] border-accent bg-paper rounded-r-lg p-6">
                <div className="flex items-center gap-2.5 mb-3">
                  <Scale size={16} className="text-accent" />
                  <h3 className="font-mono text-[11px] tracking-[0.25em] uppercase text-accent">Penalty framework</h3>
                </div>
                <p className="text-[14px] text-ink/70 leading-[1.8]">{r.penalty}</p>
              </div>
            </div>
          </div>
          <div className="bg-ink text-white rounded-lg p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <h3 className="font-heading text-xl font-bold">Need the full control catalogue?</h3>
              <p className="text-[13.5px] text-white/55 mt-1">Templates, applicability matrices and board-paper skeletons for {r.name}.</p>
            </div>
            <Link to="/contact" className="shrink-0 bg-accent text-white px-5 py-2.5 rounded-md text-[13.5px] font-semibold hover:bg-accent-dark transition-colors duration-200">Talk to us</Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
};
